// Checkout-block rich text utilities, shared by the admin (web/frontend), the
// checkout UI extension and the backend. Keep this module free of runtime
// imports (the type-only import below is erased at compile time): the
// extension bundles it into the sandboxed checkout worker.
import type {
  CheckoutBlockRichText,
  CheckoutBlockText,
  StyledSpan,
} from "@intelligems/ig-types/dtos/checkout-blocks";

/**
 * Normalises a stored text field to styled spans. A legacy plain string
 * becomes one styled span, styled as a whole by its typography `fontStyle`.
 * Rich text carries its own styles, so the legacy style never applies to it.
 */
export function toRichText(
  value: CheckoutBlockText,
  legacyFontStyle?: string
): CheckoutBlockRichText {
  if (typeof value !== "string") {
    return value;
  }

  const styledSpan: StyledSpan = { text: value };

  if (legacyFontStyle === "bold") {
    styledSpan.bold = true;
  }

  if (legacyFontStyle === "italic") {
    styledSpan.italic = true;
  }

  return [styledSpan];
}

export function richTextPlainText(value?: CheckoutBlockText | null): string {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return value.map((styledSpan) => styledSpan.text).join("");
}

export type ResolvedStyledSpan = StyledSpan & { isValue?: true };

interface TokenMatch {
  start: number;
  end: number;
  value: string;
}

function findTokenMatches(
  text: string,
  replacements: [token: string, value: string][]
): TokenMatch[] {
  const matches: TokenMatch[] = [];
  let from = 0;

  while (from < text.length) {
    let earliest: TokenMatch | null = null;

    for (const [token, value] of replacements) {
      const index = text.indexOf(token, from);

      if (index !== -1 && (earliest === null || index < earliest.start)) {
        earliest = { start: index, end: index + token.length, value };
      }
    }

    if (earliest === null) {
      break;
    }

    matches.push(earliest);
    from = earliest.end;
  }

  return matches;
}

/**
 * Substitutes `{{…}}` tokens in rich text, marking each substituted value with
 * `isValue`. Matching is exact and runs on the joined text, so a token whose
 * characters a merchant formatted differently still matches; its value takes
 * the style of the span the token starts in. An `undefined` value leaves its
 * token as literal text, an empty value drops the token.
 */
export function resolveRichTextTokens(
  richText: CheckoutBlockRichText,
  values: Record<string, string | undefined>
): ResolvedStyledSpan[] {
  // An empty token would match at every position and never advance the scan.
  const replacements = Object.entries(values).filter(
    (entry): entry is [string, string] =>
      entry[0] !== "" && entry[1] !== undefined
  );
  const matches = findTokenMatches(
    richText.map((styledSpan) => styledSpan.text).join(""),
    replacements
  );
  const resolved: ResolvedStyledSpan[] = [];
  let spanStart = 0;

  for (const { text, ...style } of richText) {
    const spanEnd = spanStart + text.length;
    let cursor = spanStart;

    for (const match of matches) {
      if (match.end <= cursor || match.start >= spanEnd) {
        continue;
      }

      if (match.start > cursor) {
        resolved.push({
          ...style,
          text: text.slice(cursor - spanStart, match.start - spanStart),
        });
      }

      if (match.start >= spanStart && match.value !== "") {
        resolved.push({ ...style, text: match.value, isValue: true });
      }

      cursor = Math.min(match.end, spanEnd);
    }

    if (cursor < spanEnd) {
      resolved.push({ ...style, text: text.slice(cursor - spanStart) });
    }

    spanStart = spanEnd;
  }

  return resolved;
}
